import { ChangeDetectionStrategy, Component, ContentChild, ContentChildren, forwardRef, Input, QueryList, TemplateRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { Field, Option, QueryBuilderConfig, Rule, RuleSet } from './models';
import {
  QueryButtonGroupDirective,
  QueryFieldDirective,
  QueryInputDirective,
  QueryOperatorDirective,
  QueryRemoveButtonDirective,
  QuerySwitchGroupDirective,
} from './template-directives';

@Component({
  selector: 'query-builder',
  templateUrl: './query-builder-compat.component.html',
  styleUrls: ['./query-builder-compat.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => QueryBuilderCompatComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => QueryBuilderCompatComponent), multi: true },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class QueryBuilderCompatComponent implements ControlValueAccessor, Validator {
  private _config: QueryBuilderConfig = { fields: {} };

  @Input() set config(config: QueryBuilderConfig) {
    if (config.fields !== this._config.fields) {
      this.fieldContexts = new WeakMap<Rule, any>();
      this.operatorContexts = new WeakMap<Rule, any>();
      this.inputContexts = new WeakMap<Rule, any>();
    }
    this._config = config;
  }

  get config(): QueryBuilderConfig {
    return this._config;
  }

  @Input() operatorMap: { [type: string]: string[] } = {};
  @Input() emptyMessage?: string;
  @Input() allowRuleset = true;

  @ContentChild(QuerySwitchGroupDirective) switchGroupTemplate?: QuerySwitchGroupDirective;
  @ContentChild(QueryButtonGroupDirective) buttonGroupTemplate?: QueryButtonGroupDirective;
  @ContentChild(QueryRemoveButtonDirective) removeButtonTemplate?: QueryRemoveButtonDirective;
  @ContentChild(QueryFieldDirective) fieldTemplate?: QueryFieldDirective;
  @ContentChild(QueryOperatorDirective) operatorTemplate?: QueryOperatorDirective;
  @ContentChildren(QueryInputDirective) inputTemplates?: QueryList<QueryInputDirective>;

  data: RuleSet = { condition: 'and', rules: [] };
  disabled = false;

  private cachedFields?: Field[];
  private cachedFieldMap?: QueryBuilderConfig['fields'];
  private readonly groupContexts = new WeakMap<RuleSet, any>();
  private readonly buttonContexts = new WeakMap<RuleSet, any>();
  private readonly switchContexts = new WeakMap<RuleSet, any>();
  private fieldContexts = new WeakMap<Rule, any>();
  private operatorContexts = new WeakMap<Rule, any>();
  private inputContexts = new WeakMap<Rule, any>();
  private readonly removeContexts = new WeakMap<Rule, any>();
  private onChange: (value: RuleSet) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: RuleSet | null): void {
    this.data = value || { condition: 'and', rules: [] };
  }

  registerOnChange(callback: (value: RuleSet) => void): void {
    this.onChange = callback;
  }

  registerOnTouched(callback: () => void): void {
    this.onTouched = callback;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};
    if (!this.config.allowEmptyRulesets && this.hasEmptyGroup(this.data)) {
      errors.empty = 'Empty rulesets are not allowed.';
    }

    const ruleErrors: any[] = [];
    this.validateGroup(this.data, ruleErrors);
    if (ruleErrors.length) {
      errors.rules = ruleErrors;
    }
    return Object.keys(errors).length ? errors : null;
  }

  asGroup(item: Rule | RuleSet): RuleSet | null {
    return 'rules' in item && Array.isArray(item.rules) ? item : null;
  }

  asRule(item: Rule | RuleSet): Rule | null {
    return this.asGroup(item) ? null : item as Rule;
  }

  groupContext(group: RuleSet, parent: RuleSet | null): any {
    if (!this.groupContexts.has(group)) {
      this.groupContexts.set(group, { $implicit: group, parent });
    }
    return this.groupContexts.get(group);
  }

  switchContext(group: RuleSet): any {
    if (!this.switchContexts.has(group)) {
      this.switchContexts.set(group, {
        $implicit: group,
        onChange: (condition: string) => this.changeCondition(group, condition),
      });
    }
    return this.switchContexts.get(group);
  }

  buttonContext(group: RuleSet, parent: RuleSet | null): any {
    if (!this.buttonContexts.has(group)) {
      this.buttonContexts.set(group, {
        $implicit: group,
        addRule: () => this.addRule(group),
        addRuleSet: this.allowRuleset ? () => this.addRuleSet(group) : null,
        removeRuleSet: parent ? () => this.removeRuleSet(group, parent) : null,
      });
    }
    return this.buttonContexts.get(group);
  }

  removeContext(rule: Rule, parent: RuleSet): any {
    if (!this.removeContexts.has(rule)) {
      this.removeContexts.set(rule, {
        $implicit: rule,
        removeRule: (removedRule: Rule) => this.removeRule(removedRule, parent),
      });
    }
    return this.removeContexts.get(rule);
  }

  fieldContext(rule: Rule): any {
    if (!this.fieldContexts.has(rule)) {
      this.fieldContexts.set(rule, {
        $implicit: rule,
        fields: this.getFields(),
        getFields: () => this.getFields(),
        onChange: (fieldValue: string, changedRule: Rule) => this.changeField(fieldValue, changedRule),
      });
    }
    return this.fieldContexts.get(rule);
  }

  operatorContext(rule: Rule): any {
    if (!this.operatorContexts.has(rule)) {
      this.operatorContexts.set(rule, {
        $implicit: rule,
        operators: this.getOperators(rule.field),
        onChange: () => this.changeOperator(rule),
      });
    }
    return this.operatorContexts.get(rule);
  }

  inputContext(rule: Rule): any {
    if (!this.inputContexts.has(rule)) {
      this.inputContexts.set(rule, {
        $implicit: rule,
        field: this.config.fields[rule.field],
        options: this.getOptions(rule.field),
        onChange: () => this.changed(),
      });
    }
    return this.inputContexts.get(rule);
  }

  inputTemplate(rule: Rule): TemplateRef<any> | null {
    const type = this.getInputType(rule.field, rule.operator);
    return (this.inputTemplates && this.inputTemplates.find(template => template.queryInputType === type)?.template) || null;
  }

  getFields(): Field[] {
    if (this.cachedFieldMap !== this.config.fields) {
      this.cachedFieldMap = this.config.fields;
      this.cachedFields = Object.keys(this.config.fields).map(value => ({ ...this.config.fields[value], value }));
    }
    return this.cachedFields || [];
  }

  getOperators(fieldName: string): string[] {
    const field = this.config.fields[fieldName];
    if (!field) {
      return [];
    }
    if (this.config.getOperators) {
      return this.config.getOperators(fieldName, field);
    }
    const defaults: { [type: string]: string[] } = {
      string: ['=', '!=', 'contains', 'like'],
      number: ['=', '!=', '>', '>=', '<', '<='],
      date: ['=', '!=', '>', '>=', '<', '<='],
      time: ['=', '!=', '>', '>=', '<', '<='],
      category: ['=', '!=', 'in', 'not in'],
      boolean: ['='],
    };
    const operators = field.operators || this.operatorMap[field.type] || defaults[field.type] || [];
    return field.nullable ? operators.concat(['is null', 'is not null']) : operators;
  }

  getOptions(fieldName: string): Option[] {
    return this.config.getOptions ? this.config.getOptions(fieldName) : this.config.fields[fieldName]?.options || [];
  }

  getInputType(fieldName: string, operator?: string): string | null {
    if (this.config.getInputType) {
      return this.config.getInputType(fieldName, operator || '');
    }
    if (operator === 'is null' || operator === 'is not null') {
      return null;
    }
    const type = this.config.fields[fieldName]?.type;
    return (operator === 'in' || operator === 'not in') && (type === 'category' || type === 'boolean') ? 'multiselect' : type || null;
  }

  addRule(parent: RuleSet): void {
    if (this.disabled || !this.getFields().length) {
      return;
    }
    if (this.config.addRule) {
      this.config.addRule(parent);
    }
    else {
      const field = this.getFields()[0];
      const operators = this.getOperators(field.value || '');
      parent.rules = parent.rules.concat([{
        field: field.value || '',
        operator: this.defaultValue(field.defaultOperator) || operators[0],
        value: this.defaultValue(field.defaultValue),
        entity: field.entity,
      }]);
    }
    this.changed();
  }

  addRuleSet(parent: RuleSet): void {
    if (this.disabled) {
      return;
    }
    if (this.config.addRuleSet) {
      this.config.addRuleSet(parent);
    }
    else {
      parent.rules = parent.rules.concat([{ condition: 'and', rules: [] }]);
    }
    this.changed();
  }

  removeRule(rule: Rule, parent: RuleSet): void {
    if (this.disabled) {
      return;
    }
    if (this.config.removeRule) {
      this.config.removeRule(rule, parent);
    }
    else {
      parent.rules = parent.rules.filter(item => item !== rule);
    }
    this.changed();
  }

  removeRuleSet(group: RuleSet, parent: RuleSet): void {
    if (this.disabled) {
      return;
    }
    if (this.config.removeRuleSet) {
      this.config.removeRuleSet(group, parent);
    }
    else {
      parent.rules = parent.rules.filter(item => item !== group);
    }
    this.changed();
  }

  changeField(fieldName: string, rule: Rule): void {
    if (this.disabled) {
      return;
    }
    const field = this.config.fields[fieldName];
    if (field && field.defaultValue !== undefined) {
      rule.value = this.defaultValue(field.defaultValue);
    }
    else {
      delete rule.value;
    }
    rule.operator = field && field.defaultOperator !== undefined
      ? this.defaultValue(field.defaultOperator)
      : this.getOperators(fieldName)[0];
    this.fieldContexts.delete(rule);
    this.operatorContexts.delete(rule);
    this.inputContexts.delete(rule);
    this.changed();
  }

  changeOperator(rule: Rule): void {
    if (this.disabled) {
      return;
    }
    const coerce = this.config.coerceValueForOperator;
    if (coerce) {
      rule.value = coerce(rule.operator || '', rule.value, rule);
    }
    else if (this.getInputType(rule.field, rule.operator) === 'multiselect' && !Array.isArray(rule.value)) {
      rule.value = [rule.value];
    }
    this.changed();
  }

  private changeCondition(group: RuleSet, condition: string): void {
    if (this.disabled) {
      return;
    }
    group.condition = condition;
    this.changed();
  }

  private defaultValue(value: any): any {
    return typeof value === 'function' ? value() : value;
  }

  private changed(): void {
    if (!this.disabled) {
      this.onTouched();
      this.onChange(this.data);
    }
  }

  private hasEmptyGroup(group: RuleSet): boolean {
    return !group.rules || !group.rules.length || group.rules.some((item) => {
      const nested = this.asGroup(item);
      return nested ? this.hasEmptyGroup(nested) : false;
    });
  }

  private validateGroup(group: RuleSet, errors: any[]): void {
    for (const item of group.rules || []) {
      const nested = this.asGroup(item);
      if (nested) {
        this.validateGroup(nested, errors);
      }
      else {
        const rule = item as Rule;
        const validator = this.config.fields[rule.field]?.validator;
        if (validator) {
          const error = validator(rule, group);
          if (error != null) {
            errors.push(error);
          }
        }
      }
    }
  }
}
